import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { NavLink, matchPath } from 'react-router-dom';
import { UserSessionContext } from 'components/UserSession';
import Routes from 'Routes';
import { APP, PAGE_NAMES, BRAND_COLOR } from 'appenv';

import UserAvatar from 'components/UserAvatar';
import IconLogout from 'components/icons/Logout';

const StyledDrawer = withStyles(() => ({
  paper: {
    width: '240px',
  },
}))(Drawer);

const UserContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 53px 0 16px;
`;

const UserName = styled.div`
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
  color: #484848;
  margin-top: 16px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledListContainer = styled.div`
  flex: 1 0 auto;
  padding-top: ${({ hideUserSession }) => (hideUserSession ? 28 : 0)}px;
`;

const StyledListItem = withStyles(() => ({
  gutters: {
    padding: '8px 24px',
  },
}))(ListItem);

const StyledNavLink = styled(NavLink)`
  color: #484848;
  text-decoration: none;
  font-style: normal;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
`;

const LogoutButtonContainer = styled.div`
  flex: 0 0 auto;
  padding: 24px;
`;

const StyledLogoutBtn = styled.div`
  color: #484848;
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const LogoutText = styled.span`
  margin-left: 12px;
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;
`;

const activeLinkStyle = {
  color: BRAND_COLOR || '#0CA58B',
};

const DrawerView = ({
  open,
  onClose,
}) => {
  const { userSession, setUserSession, canSee } = useContext(UserSessionContext);

  const onLogoutClick = () => {
    setUserSession({});
  };
  const hideUserSession = useMemo(() => (APP === 'ms624'), []);

  const WebinarLink = (() => {
    switch (APP) {
      case 'ms624': return (
        <StyledNavLink to="/about" activeStyle={activeLinkStyle}>
          {PAGE_NAMES.webinar}
        </StyledNavLink>
      );
      default: return (
        <StyledNavLink to={Routes.webinar} activeStyle={activeLinkStyle}>
          {PAGE_NAMES.webinar}
        </StyledNavLink>
      );
    }
  })();

  return (
    <StyledDrawer
      anchor="left"
      open={open}
      onClose={onClose}
    >
      {!hideUserSession && userSession?.firstName && (
        <UserContainer>
          <UserAvatar name={userSession.firstName} />
          <UserName>
            {`Hi, ${userSession.firstName}`}
          </UserName>
        </UserContainer>
      )}
      <StyledListContainer hideUserSession={hideUserSession}>
        <List>
          {
            canSee('lobby') && (
              <StyledListItem>
                <StyledNavLink
                  to={Routes.lobby}
                  activeStyle={activeLinkStyle}
                  isActive={(match, location) => (
                    matchPath(location.pathname, {
                      path: [Routes.lobby, Routes.root],
                      exact: true,
                    })
                  )}
                >
                  {PAGE_NAMES.lobby}
                </StyledNavLink>
              </StyledListItem>
            )
          }
          {
            canSee('hall') && (
              <StyledListItem>
                <StyledNavLink
                  to={Routes.hall}
                  activeStyle={activeLinkStyle}
                  isActive={(match, location) => (
                    matchPath(location.pathname, {
                      path: [Routes.hall, Routes.booth()],
                      exact: true,
                    })
                  )}
                >
                  {PAGE_NAMES.hall}
                </StyledNavLink>
              </StyledListItem>
            )
          }
          {
            canSee('webinar') && (
              <StyledListItem>
                {WebinarLink}
              </StyledListItem>
            )
          }
        </List>
      </StyledListContainer>
      {
        !hideUserSession && userSession?.firstName && (
          <LogoutButtonContainer>
            <StyledLogoutBtn onClick={onLogoutClick}>
              <IconLogout color="#484848" />
              <LogoutText>
                Logout
              </LogoutText>
            </StyledLogoutBtn>
          </LogoutButtonContainer>
        )
      }
    </StyledDrawer>
  );
};

DrawerView.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

DrawerView.defaultProps = {
  open: false,
  onClose: () => {},
};

export default DrawerView;
